<template>
  <el-dialog
    :title="$t('settings.logDialog.dialogTitle')"
    :visible="visible"
    width="75%"
    top="10vh"
    @close="closeDialog()"
    @open="onOpen()"
    custom-class="log-dialog">
    <div class="log-loading-container">
      <el-progress :percentage="refreshPercentage" status="success" :show-text="false"></el-progress>
      <el-tooltip effect="dark" :content="$t('settings.logDialog.refreshButton')" placement="right">
        <el-button type="primary" icon="el-icon-refresh" circle @click="restartPollingCircle()"></el-button>
      </el-tooltip>
    </div>
    <div ref="dialog-content" class="log-dialog-content" @scroll="onDialogContentScroll($event)">
      <div v-for="(log, index) in logs" class="log-entry" :key="index">
          <span class="log-entry-headline">
            <span :class="log.type.toLowerCase()">[{{ index + 1 }}] {{ log.date }} [{{
                log.type.trim()
              }}]</span> {{ log.logger }}
          </span>
        <span>
            {{ log.message }}
          </span>
      </div>
      <div v-if="logs.length === 0" class="logs-container-empty-state">
        <template v-if="initialLoaded">
          {{ $t('settings.logDialog.emptyState') }}
        </template>
        <template v-if="!initialLoaded">
          <i class="el-icon-loading"></i>
        </template>
      </div>
    </div>
    <div class="automatic-scroll-indicator-container">
      <el-tooltip effect="dark" :content="$t('settings.logDialog.automaticIndicator')" placement="top">
        <el-button icon="el-icon-attract"
                   round
                   :class="{'active' : hasAutomaticScroll}"
                   @click="onAutomaticScrollIndicatorClick()">
        </el-button>
      </el-tooltip>
    </div>
    <span slot="footer" class="dialog-footer">
    <el-button type="primary" @click="closeDialog()">{{ $t('base.close') }}</el-button>
  </span>
  </el-dialog>
</template>
<script>
import {logService} from "@/services/LogService.class";

export default {
  name: 'LogDialog',
  props: {
    visible: {
      default: false
    }
  },
  data() {
    return {
      intervalId: null,
      refreshPercentage: 100,
      refreshPercentageGhost: 100,
      lastRequestTimestamp: null,
      logs: [],
      hasAutomaticScroll: true,
      initialLoaded: false
    };
  },
  beforeDestroy() {
    this.endPolling();
  },
  computed: {
    dialogContentElement: function () {
      return this.$refs['dialog-content'];
    }
  },
  methods: {
    onOpen() {
      this.startPolling();
    },
    async startPolling() {
      this.endPolling();

      // get log data
      const timestamp = new Date()
      const freshLogs = await logService.get(this.lastRequestTimestamp);
      this.initialLoaded = true;
      this.logs.push(...freshLogs);
      this.lastRequestTimestamp = timestamp.toISOString();

      if (this.hasAutomaticScroll) {
        requestAnimationFrame(() => {
          this.dialogContentElement.scrollTop = this.dialogContentElement.scrollHeight;
        });
      }

      // handle progress animation
      this.intervalId = setInterval(() => {
        if (this.refreshPercentage > 0) {
          this.refreshPercentage--;
        }
        // the ghost value is necessary to consider the progress animation correctly
        this.refreshPercentageGhost--;
        if (this.refreshPercentageGhost <= -5) {
          this.restartPollingCircle();
        }
      }, 100);
    },
    endPolling() {
      this.refreshPercentage = 100;
      this.refreshPercentageGhost = 100;
      clearInterval(this.intervalId);
    },
    restartPollingCircle() {
      this.endPolling();
      setTimeout(() => {
        this.startPolling();
      }, 450); // this set timeout is necessary to consider the progress animation correctly
    },
    closeDialog() {
      this.$emit('close');
      setTimeout(() => {
        this.logs = [];
        this.initialLoaded = false;
        this.lastRequestTimestamp = null;
        this.hasAutomaticScroll = true;
        this.endPolling();
      }, 200);
    },
    onDialogContentScroll() {
      this.hasAutomaticScroll = this.dialogContentElement.scrollHeight - this.dialogContentElement.scrollTop === this.dialogContentElement.clientHeight;
    },
    onAutomaticScrollIndicatorClick() {
      this.dialogContentElement.scrollTop = this.dialogContentElement.scrollHeight;
    }
  }
};
</script>
<style scoped lang="scss">
.log-dialog {
  padding-top: 0;

  .log-loading-container {
    display: flex;
    align-items: center;
    margin-top: -28px; // trick 17
    margin-bottom: 24px;
    padding: 0 6px;

    .el-progress {
      width: 100%;
      margin-right: 12px;
    }

  }

  .log-dialog-content {
    display: flex;
    flex-direction: column;
    padding: 12px 24px;
    max-height: 50vh;
    overflow: auto;
    border: 1px solid rgba(.1, .1, .1, .1);

    .log-entry {
      display: flex;
      flex-direction: column;
      color: #000000;

      * {
        font-family: "Courier New";
      }

      .log-entry-headline {
        font-weight: bold;

        .debug {
          //color: #606266;
        }

        .warn {
          color: #f79d2e;
        }

        .error {
          color: #e63d45;
        }

        .info {
          color: #409eff;
        }
      }

      &:not(:last-child) {
        margin-bottom: 4px;
      }
    }

    .logs-container-empty-state {
      height: 100vh; // height is restricted through max height from parent container
      display: flex;
      justify-content: center;
      align-items: center;
    }
  }

  .automatic-scroll-indicator-container {
    margin: 6px 3px 0 0;
    display: flex;
    justify-content: flex-end;

    button {
      padding: 0;
      color: #606266;
      font-size: 22px;
      display: flex;
      justify-content: center;
      align-items: center;
      border: none;
      height: 28px;
      width: 28px;

      &.active {
        color: #409eff
      }
    }
  }
}
</style>
